import React, { useEffect, useState } from "react";
import { Modal, Spin, Avatar, Empty, message } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { getUserHierarchy } from "../../services/ApiService";
import "./UserHierarchyModal.css";

// Teams-style "org chart" view, opened by clicking a user's name on the
// Users page -- confirmed with the user, 2026-10-28. Ancestors render as
// a single chain (a person has only one manager), direct/indirect
// reports render as an indented tree below the focused person. Clicking
// any card re-centers the modal on that person instead of closing it,
// so the whole org can be explored without leaving the modal.
const PersonCard = ({ person, variant, onClick }) => (
  <div
    className={`hierarchy-card hierarchy-card-${variant}`}
    onClick={variant === "focus" ? undefined : onClick}
  >
    <Avatar size={36} src={person.employee_picture || undefined} icon={<UserOutlined />} />
    <div className="hierarchy-card-text">
      <span className="hierarchy-card-name">{person.name}</span>
      {person.designation && <span className="hierarchy-card-designation">{person.designation}</span>}
    </div>
  </div>
);

const ChildrenTree = ({ nodes, onSelect }) => {
  if (!nodes || nodes.length === 0) return null;
  return (
    <div className="hierarchy-children">
      {nodes.map((node) => (
        <div key={node.employee_id} className="hierarchy-branch">
          <PersonCard person={node} variant="report" onClick={() => onSelect(node.employee_id)} />
          <ChildrenTree nodes={node.children} onSelect={onSelect} />
        </div>
      ))}
    </div>
  );
};

const UserHierarchyModal = ({ open, employeeId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [currentEmployeeId, setCurrentEmployeeId] = useState(employeeId);

  useEffect(() => {
    if (open) setCurrentEmployeeId(employeeId);
  }, [open, employeeId]);

  useEffect(() => {
    if (!open || !currentEmployeeId) return;
    const fetchHierarchy = async () => {
      setLoading(true);
      try {
        const res = await getUserHierarchy(currentEmployeeId);
        if (res?.responseCode === 200) {
          setData(res);
        } else {
          message.error(res?.responseMessage || "Could not load this user's hierarchy.");
        }
      } catch (error) {
        console.error("Error fetching user hierarchy:", error);
        message.error("Could not load this user's hierarchy.");
      } finally {
        setLoading(false);
      }
    };
    fetchHierarchy();
  }, [open, currentEmployeeId]);

  return (
    <Modal
      title="Reporting Hierarchy"
      open={open}
      onCancel={onClose}
      footer={null}
      width={680}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <div className="hierarchy-modal-body">
          {data ? (
            <>
              {data.ancestors.length > 0 && (
                <div className="hierarchy-ancestors">
                  {data.ancestors.map((a) => (
                    <React.Fragment key={a.employee_id}>
                      <PersonCard
                        person={a}
                        variant="ancestor"
                        onClick={() => setCurrentEmployeeId(a.employee_id)}
                      />
                      <div className="hierarchy-chain-line" />
                    </React.Fragment>
                  ))}
                </div>
              )}

              <PersonCard person={data.focus} variant="focus" />

              <div className="hierarchy-descendants">
                {data.children.length > 0 ? (
                  <ChildrenTree nodes={data.children} onSelect={setCurrentEmployeeId} />
                ) : (
                  <div className="hierarchy-empty-note">No direct reports on record.</div>
                )}
              </div>
            </>
          ) : (
            !loading && <Empty description="Could not load hierarchy." />
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default UserHierarchyModal;
